/**
 * Report date range state, backed by the URL.
 *
 * Every report page keeps its date range here so that (1) a link carrying
 * `?startDate=…&endDate=…` — the Pay Period detail page's "View Payroll
 * Report", or a range copied from one report into another — lands on those
 * dates instead of the last-30-days default, and (2) the chosen range survives
 * a reload or a back-navigation.
 *
 * Both YYYY-MM-DD values and full ISO instants are accepted. Pay periods are
 * stored as local midnight → local end of day, so an instant is converted with
 * the viewer's LOCAL calendar fields: `.split('T')[0]` on an end-of-day
 * timestamp west of UTC names the next day (a period ending Sep 15 read as
 * Sep 16).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toDateInputValue } from '../../utils/dateFormatting';
import { getDefaultDateRange, type DateRange } from './ReportFilters';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Normalise a URL date param to the YYYY-MM-DD form the date inputs use. */
export function parseDateParam(value: string | null): string | null {
  if (!value) return null;
  if (DATE_ONLY.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateInputValue(value);
}

/** The URL is the only source of truth; the setter writes it, the getter reads it. */
export function useReportDateRange(): [DateRange, (range: DateRange) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const dateRange = useMemo<DateRange>(() => {
    const startDate = parseDateParam(searchParams.get('startDate'));
    const endDate = parseDateParam(searchParams.get('endDate'));
    if (startDate && endDate && startDate <= endDate) {
      return { startDate, endDate };
    }
    return getDefaultDateRange();
  }, [searchParams]);

  const setDateRange = useCallback(
    (range: DateRange) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('startDate', range.startDate);
          next.set('endDate', range.endDate);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return [dateRange, setDateRange];
}
